import React, { useEffect, useState } from 'react';
import { dashboardStorage } from '../../services/localStorage.service';
import './Dashboard.css';

// Icon Components
const SalesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 6H18L17 13H7L6 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 13L5 22H19L17 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="8.5" cy="20" r="1.5" fill="currentColor"/>
    <circle cx="15.5" cy="20" r="1.5" fill="currentColor"/>
  </svg>
);

const PurchaseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 9L12 2L21 9V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ProfitIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 21L12 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3V21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 16L12 11L16 15L21 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Chart Components
const SimpleBarChart = ({ data, maxValue, labelKey, valueKey, color }) => {
  const max = Math.max(...data.map(d => Math.abs(d[valueKey])), 1);
  
  return (
    <div className="simple-bar-chart">
      <div className="chart-bars">
        {data.map((item, index) => {
          const height = max > 0 ? (Math.abs(item[valueKey]) / max) * 100 : 0;
          const isNegative = item[valueKey] < 0;
          return (
            <div key={index} className="chart-bar-container">
              <div className="chart-bar-wrapper">
                <div
                  className={`chart-bar ${isNegative ? 'negative' : ''}`}
                  style={{
                    height: `${height}%`,
                    backgroundColor: color || (isNegative ? '#ef4444' : '#14b8a6'),
                  }}
                  title={`${item[labelKey]}: ${item[valueKey] >= 0 ? '₹' : '-₹'}${Math.abs(item[valueKey]).toLocaleString('en-IN')}`}
                />
              </div>
              <div className="chart-label">{item[labelKey]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DonutChart = ({ data }) => {
  const colors = ['#2563eb', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899'];
  const total = data.reduce((sum, item) => sum + item.amount, 0);
  
  if (total === 0) {
    return (
      <div className="donut-chart">
        <div className="donut-chart-inner">
          <div className="donut-chart-center">
            <span className="donut-total">No Data</span>
          </div>
        </div>
      </div>
    );
  }
  
  let currentPercentage = 0;
  const segments = data.map((item, index) => {
    const percentage = (item.amount / total) * 100;
    const start = currentPercentage;
    currentPercentage += percentage;
    return {
      ...item,
      start,
      end: currentPercentage,
      color: colors[index % colors.length],
    };
  });
  
  const gradientParts = segments.map(s => 
    `${s.color} ${s.start}% ${s.end}%`
  ).join(', ');
  
  return (
    <div className="donut-chart" style={{
      background: `conic-gradient(${gradientParts})`
    }}>
      <div className="donut-chart-inner">
        <div className="donut-chart-center">
          <span className="donut-total">{total > 0 ? '100%' : '0%'}</span>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [outstandingBalances, setOutstandingBalances] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [topSelling, setTopSelling] = useState([]);
  const [profitData, setProfitData] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);

  useEffect(() => {
    const loadDashboardData = () => {
      try {
        // Calculate stats from localStorage
        const stats = dashboardStorage.calculateStats();
        setDashboardData(stats);
        
        // Load outstanding balances
        const outstanding = dashboardStorage.getOutstandingBalances();
        setOutstandingBalances(outstanding);
        
        // Load category sales
        const categories = dashboardStorage.getCategorySales();
        setCategorySales(categories);
        
        // Load top selling categories
        const top = dashboardStorage.getTopSellingCategories();
        setTopSelling(top);
        
        // Load profit data
        const profit = dashboardStorage.getProfitData();
        setProfitData(profit);
        
        // Load cash flow
        const cash = dashboardStorage.getCashFlow();
        setCashFlow(cash);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Fallback to default values
        setDashboardData({
          todaySales: 0,
          todayPurchases: 0,
          todayProfit: 0,
          monthlyProfit: 0,
          outstandingBalance: 0,
          admittedPatients: 0,
          trends: {
            salesTrend: 0,
            purchasesTrend: 0,
            profitTrend: 0,
            monthlyProfitTrend: 0,
          },
        });
        setOutstandingBalances([]);
        setCategorySales([]);
        setTopSelling([]);
        setProfitData({ dailyProfit: [], monthlyProfit: [] });
        setCashFlow({ collected: 0, remaining: 0, total: 0 });
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
    
    // Refresh data every 30 seconds for live updates
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatTrend = (trend) => {
    if (trend === 0) return '0%';
    const sign = trend > 0 ? '+' : '';
    return `${sign}${trend.toFixed(1)}%`;
  };

  const kpiCards = dashboardData ? [
    { 
      label: "Today's Total Sales", 
      value: formatCurrency(dashboardData.todaySales), 
      trend: formatTrend(dashboardData.trends.salesTrend),
      trendLabel: "vs yesterday",
      trendType: dashboardData.trends.salesTrend >= 0 ? "positive" : "negative",
      icon: SalesIcon
    },
    { 
      label: "Today's Total Purchases", 
      value: formatCurrency(dashboardData.todayPurchases), 
      trend: formatTrend(dashboardData.trends.purchasesTrend),
      trendLabel: "vs yesterday",
      trendType: dashboardData.trends.purchasesTrend <= 0 ? "positive" : "negative",
      icon: PurchaseIcon
    },
    { 
      label: "Today's Profit", 
      value: formatCurrency(dashboardData.todayProfit), 
      trend: formatTrend(dashboardData.trends.profitTrend),
      trendLabel: "vs yesterday",
      trendType: dashboardData.trends.profitTrend >= 0 ? "positive" : "negative",
      icon: ProfitIcon
    },
    { 
      label: "This Month's Profit", 
      value: formatCurrency(dashboardData.monthlyProfit), 
      trend: formatTrend(dashboardData.trends.monthlyProfitTrend),
      trendLabel: "vs last month",
      trendType: dashboardData.trends.monthlyProfitTrend >= 0 ? "positive" : "negative",
      icon: ChartIcon
    },
  ] : [];

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1 className="page-title">Dashboard Overview</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="page-title">Dashboard Overview</h1>
      </div>

      <div className="kpi-grid">
        {kpiCards.map((card, index) => {
          const IconComponent = card.icon;
          return (
            <div key={index} className="kpi-card">
              <div className="kpi-header">
                <div className="kpi-icon">
                  <IconComponent />
                </div>
                <div className="kpi-trend">
                  <span className={`kpi-trend-value ${card.trendType}`}>
                    {card.trendType === 'positive' ? '↑' : '↓'} {card.trend}
                  </span>
                  <span className="kpi-trend-label">{card.trendLabel}</span>
                </div>
              </div>
              <div className="kpi-content">
                <h3 className="kpi-value">{card.value}</h3>
                <p className="kpi-label">{card.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-section">
          <div className="dashboard-card">
            <h2 className="card-title">Category-wise Sales Totals</h2>
            <div className="chart-container">
              <DonutChart data={categorySales} />
              <div className="chart-legend">
                {categorySales.map((item, index) => {
                  const colors = ['#2563eb', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899'];
                  return (
                    <div key={index} className="legend-item">
                      <span 
                        className="legend-color" 
                        style={{ backgroundColor: colors[index % colors.length] }}
                      ></span>
                      <span className="legend-label">
                        {item.name}: {item.percentage}% ({formatCurrency(item.amount)})
                      </span>
                    </div>
                  );
                })}
                {categorySales.length === 0 && (
                  <div style={{ color: '#64748b', fontStyle: 'italic' }}>No category data available</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <div className="dashboard-card admitted-patients">
            <h2 className="card-title">Total Admitted Patients</h2>
            <div className="admitted-patients-content">
              <div className="admitted-patients-number">
                {dashboardData?.admittedPatients || 0}
              </div>
              <p className="admitted-patients-subtitle">Patients currently admitted</p>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <div className="dashboard-card">
          <h2 className="card-title">Profit Dashboard: Daily & Monthly Trends</h2>
          <div className="charts-grid">
            <div className="chart-box">
              <h3 className="chart-title">Daily Profit (Last 7 Days)</h3>
              {profitData && profitData.dailyProfit && profitData.dailyProfit.length > 0 ? (
                <SimpleBarChart
                  data={profitData.dailyProfit.map(d => ({
                    ...d,
                    date: new Date(d.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                  }))}
                  maxValue={Math.max(...profitData.dailyProfit.map(d => Math.abs(d.profit)))}
                  labelKey="date"
                  valueKey="profit"
                  color="#14b8a6"
                />
              ) : (
                <div className="chart-placeholder">
                  <p>No profit data available</p>
                </div>
              )}
            </div>
            <div className="chart-box">
              <h3 className="chart-title">Monthly Profit (Last 6 Months)</h3>
              {profitData && profitData.monthlyProfit && profitData.monthlyProfit.length > 0 ? (
                <SimpleBarChart
                  data={profitData.monthlyProfit}
                  maxValue={Math.max(...profitData.monthlyProfit.map(d => Math.abs(d.profit)))}
                  labelKey="month"
                  valueKey="profit"
                  color="#14b8a6"
                />
              ) : (
                <div className="chart-placeholder">
                  <p>No profit data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-section">
          <div className="dashboard-card">
            <h2 className="card-title">Top-Selling Categories/Patient Types</h2>
            <div className="top-selling-list">
              {topSelling.length > 0 ? (
                topSelling.map((item, index) => (
                  <div key={index} className="top-selling-item">
                    <span className="top-selling-category">{item.category}</span>
                    <span className="top-selling-amount">{formatCurrency(item.amount)}</span>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                  No sales data available
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <div className="dashboard-card">
            <h2 className="card-title">Outstanding Balances Summary</h2>
            <div className="outstanding-table">
              {outstandingBalances.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                  No outstanding balances found
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Amount</th>
                      <th>Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstandingBalances.map((item, index) => {
                      const dueDate = new Date(item.dueDate);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isOverdue = dueDate < today;
                      
                      return (
                        <tr key={item.saleId || index} style={isOverdue ? { backgroundColor: '#fee2e2' } : {}}>
                          <td>{item.patient}</td>
                          <td className="amount-cell" style={{ fontWeight: '600', color: isOverdue ? '#dc2626' : '#1e293b' }}>
                            {formatCurrency(item.amount)}
                          </td>
                          <td style={{ color: isOverdue ? '#dc2626' : '#1e293b' }}>
                            {item.dueDate}
                            {isOverdue && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#dc2626' }}>(Overdue)</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {outstandingBalances.length > 0 && (
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '4px', fontSize: '14px', color: '#64748b' }}>
                Total Outstanding: <strong style={{ color: '#1e293b' }}>
                  {formatCurrency(outstandingBalances.reduce((sum, item) => sum + item.amount, 0))}
                </strong>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <div className="dashboard-card">
          <h2 className="card-title">Cash Flow: Collected vs. Remaining</h2>
          {cashFlow && (
            <div className="cash-flow-container">
              <div className="cash-flow-stats">
                <div className="cash-flow-item">
                  <div className="cash-flow-label">Collected</div>
                  <div className="cash-flow-value collected">{formatCurrency(cashFlow.collected)}</div>
                  <div className="cash-flow-percentage">
                    {cashFlow.total > 0 ? Math.round((cashFlow.collected / cashFlow.total) * 100) : 0}%
                  </div>
                </div>
                <div className="cash-flow-item">
                  <div className="cash-flow-label">Remaining</div>
                  <div className="cash-flow-value remaining">{formatCurrency(cashFlow.remaining)}</div>
                  <div className="cash-flow-percentage">
                    {cashFlow.total > 0 ? Math.round((cashFlow.remaining / cashFlow.total) * 100) : 0}%
                  </div>
                </div>
                <div className="cash-flow-item total">
                  <div className="cash-flow-label">Total</div>
                  <div className="cash-flow-value">{formatCurrency(cashFlow.total)}</div>
                </div>
              </div>
              <div className="cash-flow-bar">
                <div 
                  className="cash-flow-bar-fill collected"
                  style={{ 
                    width: cashFlow.total > 0 ? `${(cashFlow.collected / cashFlow.total) * 100}%` : '0%' 
                  }}
                />
                <div 
                  className="cash-flow-bar-fill remaining"
                  style={{ 
                    width: cashFlow.total > 0 ? `${(cashFlow.remaining / cashFlow.total) * 100}%` : '0%' 
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
